/**
 * Oh the wonderful world of images...
 *
 * We have decided as a team to create the image post-processing abstraction layer in the
 * frontend and have it be purposefully built upon imgix. If we decide to move away from
 * imgix OR move our abstraction to the BE; we will refactor this module and the components
 * that depend on it.
 *
 * Assumptions:
 * 1) Images can be retrieved via imgix
 * 2) Images reside on S3 as an origin to imgix
 * 3) Images can be resized on-the-fly via imgix
 * 4) Images can have filters applied (i.e. greyscale) on-the-fly via imgix
 * 5) Clients should only request images having a size no greater than can be display on that user agent
 */
import { isNil } from 'lodash';
import { PixelRatio } from 'react-native';

const s3Staginghost = /coast-staging-uploads.s3.us-(west|east)-[12].amazonaws.com/;
const s3Prodhost = /coast-production-files.s3.us-(west|east)-[12].amazonaws.com/;
const imgixHost = /imgix.net/;

function asImgix(uri: string): string {
  return uri
    ? uri
        .replace(s3Staginghost, 'coast-staging.imgix.net')
        .replace(s3Prodhost, 'coast-production.imgix.net')
    : uri;
}

// merges any existing query params with passed params
// only accepts imgix url.
export function mergeImgixParams(uri: string, params: { [key: string]: any }): string {
  if (uri && imgixHost.test(uri)) {
    const [base, args] = uri.split('?');
    let a = args ? args.split('&') : [];
    Object.keys(params).forEach(k => a.push(params[k] ? `${k}=${params[k]}` : k));
    if (a.length) {
      return `${base}?${a.join('&')}`;
    }
    return base;
  }
  return uri;
}

// builds imgix url with any params and accepts s3 or imgix url
// light wrapper for mergeImgixParams above
export function buildImgixUrl(url: string, params: { [key: string]: any }): string {
  return mergeImgixParams(asImgix(url), params);
}

// helper to get an image that proportionately fits inside `dims`,
// adjusted for dpr and compressed. can take s3 or imgix url.
export function containedImageUrl(url: string, dims: [number, number]): string {
  return buildImgixUrl(url, {
    w: dims[0],
    h: dims[1],
    dpr: PixelRatio.get(),
    fit: 'max',
    auto: 'compress',
  });
}

// given s3 url, returns imgix for full size image
export function fullSizeImageUri(uri: string) {
  return buildImgixUrl(uri, { dpr: PixelRatio.get(), fit: 'max', auto: 'compress' });
}

export function jsonImageUri(uri: string) {
  return imgixHost.test(asImgix(uri)) ? buildImgixUrl(uri, { fm: 'json' }) : null;
}

// given s3 url, returns imgix download for full size image
export function downloadFullSizeImageUri(uri: string) {
  return `${buildImgixUrl(uri, { dl: null })}`;
}

// helper to get an image sized to width/height.
// accepts s3 or imgix url.
// adjusted for dpr.
export function resizeImageUri<T extends string | null | undefined>(
  uri: T,
  width?: number,
  height?: number,
  minWidth?: number,
  minHeight?: number,
): T {
  if (isNil(uri)) {
    return uri;
  }

  const dpr = PixelRatio.get();
  let params: { [key: string]: any } = {
    dpr,
  };
  if (width) {
    params.w = width;
  }
  if (height) {
    params.h = height;
  }
  if (minWidth) {
    params['min-w'] = minWidth;
  }
  if (minHeight) {
    params['min-h'] = minHeight;
  }
  return mergeImgixParams(asImgix(uri), params) as T;
}

export function greyscaleImageUri(uri: string): string {
  return mergeImgixParams(asImgix(uri), { sat: '-100' });
}
